import React, { ReactElement, useContext, useMemo, useRef, useState } from 'react';
import DocumentsGrid from '../DocumentsGrid/DocumentsGrid';
import DocumentUploadArea from '../DocumentUploadArea/DocumentUploadArea';
import { IChecklistItem, IChecklistItemGroup, IDocument } from 'src/shared/types/Documents';
import styles from './DocumentUploadPage.module.scss';
import { useGetCaseDetails } from 'src/shared/queries/useGetCaseDetails';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import PassportUploadArea from '../PassportUploadArea/PassportUploadArea';
import { createChecklistItemGroup } from 'src/shared/services/fn-api/Cases';
import PassportGroup from '../PassportGroup/PassportGroup';
import { removePassportDocuments } from 'src/shared/services/document-api/Documents';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { Toast } from '@ds';
import { NoDocumentsFound } from '../NoDocumentsFound/NoDocumentsFound';

interface IDescriptionProps {
  description?: string;
  minDescriptionLength?: number;
}

const MIN_DESCRIPTION_LENGTH = 750;

const Description = (props: IDescriptionProps): ReactElement => {
  if (!props.description) return <p></p>;

  if (props.description.length < (props.minDescriptionLength ?? 0))
    return <pre className="member-checklist-desc">{props.description}</pre>;

  const [showFullDescription, setShowFullDescription] = useState<boolean>(false);

  const tempdesc = props.description.substring(0, props.minDescriptionLength);
  const shortDescription = tempdesc.substring(0, tempdesc.lastIndexOf(' '));

  return (
    <pre className="member-checklist-desc">
      {`${showFullDescription ? props.description : shortDescription} `}
      <strong className="redirect-link" onClick={() => setShowFullDescription(!showFullDescription)}>
        {showFullDescription ? 'Read less' : 'Read more'}
      </strong>
    </pre>
  );
};

const DocumentUploadPage: React.FC<{
  checklistItem: IChecklistItem;
  onRefresh: () => void;
  isPassport?: boolean;
  onRefreshAdditionalDocs: () => void;
  setCurrentItem?: (item: IChecklistItem) => void;
}> = ({ checklistItem, onRefresh, onRefreshAdditionalDocs, setCurrentItem, isPassport = false }) => {
  const { caseId } = useContext(BeneDocAdditionalContext);
  const { data: caseDetails } = useGetCaseDetails(caseId ?? 0);
  const isCaseActive = caseDetails?.isActive ?? false;
  const toastRef = useRef<IToastr>(null);

  const documents = useMemo(() => {
    return (checklistItem?.checklistItemDocuments ?? [])
      .map((item) => item.document)
      .filter((doc): doc is IDocument => doc !== undefined);
  }, [checklistItem?.checklistItemDocuments]);

  const memberName = checklistItem.beneficiaryId
    ? checklistItem.beneficiary?.fullName ||
      `${checklistItem.beneficiary?.firstName} ${checklistItem.beneficiary?.lastName}`
    : checklistItem.dependent?.dependentFullName ||
      `${checklistItem.dependent?.dependentFirstName} ${checklistItem.dependent?.dependentLastName}`;

  const addChecklistItemGroupForPassport = async () => {
    const checklistItemGroup = await createChecklistItemGroup(checklistItem.id);
    const item: IChecklistItem = { ...checklistItem };
    checklistItemGroup.showNewPassportGroup = true;
    (item.checklistItemGroups ??= []).unshift(checklistItemGroup);

    if (setCurrentItem) {
      setCurrentItem(item);
    }
  };

  const onDeletePassportGroup = async (group: IChecklistItemGroup, hideToast?: boolean) => {
    if (group) {
      if (!hideToast) {
        toastRef.current?.show({ message: 'Deleting...', severity: 'info', sticky: true });
      }
      try {
        if (!caseId) {
          console.error('caseId is undefined. Cannot remove passport documents.');
          return;
        }
        let values = {
          caseId: caseId,
          checklistItemGroupId: group.id,
        };
        await removePassportDocuments(values);
      } catch (error) {
      } finally {
        if (!hideToast) {
          toastRef.current?.clear();
          toastRef.current?.show({
            message: 'Deleted',
            severity: 'success',
            sticky: false,
          });
        }
        onRefresh && onRefresh();
      }
    }
  };

  return (
    <div className={styles['case-docs-page']}>
      <p className={styles['title']}>{`${checklistItem?.checklistDefinition?.longTitle} for ${memberName}`}</p>

      {isCaseActive && (
        <div className={styles['description']}>
          <Description
            description={checklistItem.checklistDefinition?.description}
            minDescriptionLength={MIN_DESCRIPTION_LENGTH}
          />
        </div>
      )}

      {!checklistItem.checklistItemGroups ||
        (checklistItem.checklistItemGroups.length === 0 && (
          <div className={styles['non-passport']}>
            <DocumentsGrid
              documents={documents}
              currentItem={checklistItem}
              showMenu={isCaseActive}
              onRefetchDocuments={onRefresh}
              emptyMessage={<NoDocumentsFound />}
              onRefetchAdditionalDocuments={onRefreshAdditionalDocs}
            />
          </div>
        ))}
      {isPassport && (
        <div className="passport">
          {checklistItem.checklistItemGroups?.map((group, index) => (
            <PassportGroup
              key={index}
              group={group}
              checklistItem={checklistItem}
              onRefreshChecklist={onRefresh}
              onDeletePassportGroup={onDeletePassportGroup}
            />
          ))}
        </div>
      )}

      {isCaseActive && isPassport && (
        <>
          {checklistItem.checklistItemGroups?.length !== 0 && <p className={styles['add-more']}>ADD MORE</p>}

          <PassportUploadArea
            allowFromCobalt
            checklistItem={checklistItem}
            addChecklistItemGroupForPassport={addChecklistItemGroupForPassport}
            onRefreshChecklist={onRefresh}
          />
        </>
      )}
      {isCaseActive && !isPassport && (
        <>
          {documents.length > 0 && <p className={styles['add-more']}>ADD MORE</p>}
          <DocumentUploadArea allowFromCobalt checklistItem={checklistItem} onUploadComplete={onRefresh} />
        </>
      )}
      <Toast ref={toastRef} />
    </div>
  );
};

export default DocumentUploadPage;
